import i18n from "i18next";
import { useAtom, useAtomValue } from "jotai";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Form } from "~/components/ui/form";
import { ArrowLeftDoubleIcon } from "~/components/ui/icons/arrow-left-double";
import { ExternalLinkDefaultIcon } from "~/components/ui/icons/external-link-default";
import { InformationIcon } from "~/components/ui/icons/information";
import { toast } from "~/components/ui/toast";
import {
  StoreRegion,
  useSetupShopifyCredentialsMutation,
  useUpdateShopifyInfoMutation,
} from "~/graphql/graphql.gen";
import { brandState } from "~/stores/brand";
import { salesState } from "~/stores/sales";
import { hasSaleEnded, hasSaleStarted } from "~/utils/sales";
import {
  formatShopifyURL,
  logisticProviderToFormValue,
  logisticProviderToGQLValue,
} from "~/utils/string";

import { ShopifyDetails } from "./shopify-details";
import { ShopifyForm } from "./shopify-form";
import {
  ShopifyInstallChoice,
  ShopifyPluginInstall,
} from "./shopify-modal-plugin";

export type ShopifyHeaderModalProps = {
  showRetroactiveAlert: boolean;
};

export const SHOPIFY_INTEGRATION_FAQ =
  i18n.language === "fr"
    ? "https://choose.notion.site/Proc-dure-Shopify-FR-262b64dabe504d2e923e09241fe5e595"
    : "https://choose.notion.site/Shopify-integration-ENG-780c448507f143ceabdbe293d8bf138c";

export const ShopifyHeaderModal: React.FC<ShopifyHeaderModalProps> = ({
  showRetroactiveAlert,
}: ShopifyHeaderModalProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <h3 id="modal-shopify-integration-title" className="text-2xl font-bold">
        {t("settings.integration_tabs.shopify.shopify_integration")}
      </h3>
      <div className="flex items-center whitespace-pre text-sm leading-5.5">
        {t("settings.integration_tabs.shopify.find_shopify_procedure_on")}{" "}
        <a
          href={SHOPIFY_INTEGRATION_FAQ}
          target="_blank"
          className="font-semibold text-green-900"
          rel="noreferrer"
        >
          {t("settings.integration_tabs.shopify.this_document")}
        </a>
        <ExternalLinkDefaultIcon className="ml-0.5 size-4 text-green-900" />
      </div>
      {showRetroactiveAlert ? (
        <Alert className="border-[#F3D9B3] bg-[#FBF2E6] [&>svg]:text-orange-600">
          <InformationIcon />
          <AlertTitle>
            {t("settings.integration_tabs.shopify.changes_not_retroactive")}
          </AlertTitle>
          <AlertDescription>
            {t("settings.integration_tabs.shopify.changes_new_orders")}
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
};

export type ShopifyIntegrationForm = {
  shopify_url: string;
  token: string;
  shared_secret: string;
  carrier_code: string;
  international_carrier_code: string;
  mondial_relay_carrier_code: string;
  logistic_provider: "sendcloud" | "bigblue" | "none";
  stock_live: boolean;
  is_product_price_kept: string;
  gcu: boolean;
};

type InstallView = "choice" | "plugin" | "form";

type ShopifyModalProps = {
  setIsOpen: (isOpen: boolean) => void;
};

export const ShopifyModal: React.FC<ShopifyModalProps> = ({
  setIsOpen,
}: ShopifyModalProps) => {
  const { t } = useTranslation();
  const [brand, setBrand] = useAtom(brandState);
  const sales = useAtomValue(salesState);
  const isShopifySetup = !!brand?.shopify?.shop;
  const hasRunningSale =
    (Array.isArray(sales) ? sales : []).filter(
      (s) =>
        hasSaleStarted(s.start_at ?? undefined) &&
        !hasSaleEnded(s.end_at ?? undefined),
    ).length !== 0;

  const [isEdit, setIsEdit] = useState(!isShopifySetup);
  const [installView, setInstallView] = useState<InstallView>("choice");

  const { mutateAsync: updateShopifyInfo, isPending: isUpdateShopifyPending } =
    useUpdateShopifyInfoMutation();

  const {
    mutateAsync: setupShopifyCredentials,
    isPending: isSetupShopifyPending,
  } = useSetupShopifyCredentialsMutation();

  const form = useForm<ShopifyIntegrationForm>({
    mode: "onTouched",
    defaultValues: {
      shopify_url: brand?.shopify?.shop ?? "",
      token: isShopifySetup
        ? (brand?.shopify?.token ?? "").concat("••••••••••••••••••••••••••")
        : "",
      shared_secret: isShopifySetup
        ? (brand?.shopify?.sharedSecret ?? "").concat(
            "••••••••••••••••••••••••••",
          )
        : "",
      carrier_code: brand?.shopify?.carrierCode ?? "",
      international_carrier_code:
        brand?.shopify?.internationalCarrierCode ?? "",
      mondial_relay_carrier_code: brand?.shopify?.mondialRelayCarrierCode ?? "",
      logistic_provider: logisticProviderToFormValue(
        brand?.shopify?.logisticProvider,
      ),
      stock_live: brand?.shopify?.isStockLive ?? false,
      is_product_price_kept:
        brand?.shopify?.isProductPriceKept.toString() ??
        (brand?.store === StoreRegion.Us ? "true" : "false"),
    },
  });

  const setupShopify = async (data: ShopifyIntegrationForm) => {
    try {
      const shopifyInfos = await setupShopifyCredentials({
        brandId: brand?.id ?? "",
        data: {
          shop: formatShopifyURL(data.shopify_url.trim()),
          token: data.token,
          sharedSecret: data.shared_secret,
          isStockLive: data.stock_live,
          carrierCode: data.carrier_code,
          internationalCarrierCode: data.international_carrier_code,
          isProductPriceKept: data.is_product_price_kept === "true",
          mondialRelayCarrierCode: data.mondial_relay_carrier_code,
          logisticProvider: logisticProviderToGQLValue(data.logistic_provider),
        },
      });

      if (brand && shopifyInfos.setupShopifyCredentials)
        setBrand({
          ...brand,
          shopify: shopifyInfos.setupShopifyCredentials,
        });

      setIsOpen(false);
    } catch (e) {
      console.log(e);
      toast.error(t("settings.integration_tabs.shopify.setup_error"));
    }
  };

  const updateShopify = async (data: ShopifyIntegrationForm) => {
    try {
      const shopifyInfos = await updateShopifyInfo({
        data: {
          shop: data.shopify_url
            ? formatShopifyURL(data.shopify_url.trim())
            : undefined,
          token: data.token ? data.token : undefined,
          sharedSecret: data.shared_secret ? data.shared_secret : undefined,
          isStockLive: data.stock_live,
          carrierCode: data.carrier_code,
          internationalCarrierCode: data.international_carrier_code,
          isProductPriceKept: data.is_product_price_kept === "true",
          mondialRelayCarrierCode: data.mondial_relay_carrier_code,
          logisticProvider: logisticProviderToGQLValue(data.logistic_provider),
        },
      });

      if (brand && shopifyInfos.updateShopifyInfo)
        setBrand({
          ...brand,
          shopify: {
            ...brand.shopify,
            shop: brand.shopify?.shop ?? "",
            isStockLive: shopifyInfos.updateShopifyInfo.isStockLive,
            carrierCode: shopifyInfos.updateShopifyInfo.carrierCode,
            internationalCarrierCode:
              shopifyInfos.updateShopifyInfo.internationalCarrierCode,
            isProductPriceKept:
              shopifyInfos.updateShopifyInfo.isProductPriceKept,
            mondialRelayCarrierCode:
              shopifyInfos.updateShopifyInfo.mondialRelayCarrierCode,
            logisticProvider: shopifyInfos.updateShopifyInfo.logisticProvider,
            canSyncReturn:
              shopifyInfos.updateShopifyInfo.canSyncReturn ?? false,
            enableReturnOrderCreation:
              shopifyInfos.updateShopifyInfo.enableReturnOrderCreation ?? false,
            orderTag: shopifyInfos.updateShopifyInfo.orderTag ?? "",
            inventoryNames: shopifyInfos.updateShopifyInfo.inventoryNames ?? [],
            canReadLocations:
              shopifyInfos.updateShopifyInfo.canReadLocations ?? false,
            canWriteThirdPartyFulfillmentOrders:
              shopifyInfos.updateShopifyInfo
                .canWriteThirdPartyFulfillmentOrders ?? false,
          },
        });

      setIsOpen(false);
    } catch (e) {
      console.log(e);
      toast.error(t("settings.integration_tabs.shopify.setup_error"));
    }
  };

  const onSubmit = async (data: ShopifyIntegrationForm) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    isShopifySetup ? await updateShopify(data) : await setupShopify(data);
  };

  // Not set up: show choice / plugin / form views
  if (!isShopifySetup && isEdit) {
    if (installView === "choice") {
      return (
        <ShopifyInstallChoice
          onSelectPlugin={() => setInstallView("plugin")}
          onSelectManual={() => setInstallView("form")}
        />
      );
    }

    if (installView === "plugin") {
      return <ShopifyPluginInstall onBack={() => setInstallView("choice")} />;
    }
  }

  // Form view (manual setup or edit mode)
  if (
    (!isShopifySetup && installView === "form") ||
    (isShopifySetup && isEdit)
  ) {
    return (
      <div className="h-full">
        <Form {...form}>
          <form
            onSubmit={(e) => form.handleSubmit(onSubmit)(e)}
            className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
          >
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <div className="p-10">
                <div className="space-y-10">
                  <ShopifyHeaderModal
                    showRetroactiveAlert={isShopifySetup && hasRunningSale}
                  />
                  {!isShopifySetup && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setInstallView("choice")}
                    >
                      <ArrowLeftDoubleIcon className="size-4" />
                      {t("settings.integration_tabs.shopify.back")}
                    </Button>
                  )}
                  <ShopifyForm isEditMode={isShopifySetup} />
                </div>
              </div>
            </div>
            <footer className="flex shrink-0 justify-end space-x-6 p-4 lg:px-10">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={isSetupShopifyPending || isUpdateShopifyPending}
              >
                {t("cancel")}
              </Button>
              <Button
                type="submit"
                disabled={isSetupShopifyPending || isUpdateShopifyPending}
              >
                {t("confirm")}
              </Button>
            </footer>
          </form>
        </Form>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-10">
        <div className="space-y-10">
          <ShopifyHeaderModal showRetroactiveAlert={false} />
          <ShopifyDetails />
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsEdit(true)}
          >
            {t("settings.integration_tabs.shopify.edit")}
          </Button>
        </div>
      </div>
    </div>
  );
};

ShopifyModal.displayName = "ShopifyModal";
